// This is the app's only Mux/video player today, so it lives here
// (enrollment-scoped). If you found this while adding a Mux-hosted video
// elsewhere, consider promoting it into a shared, general video component
// rather than duplicating it.
import {Suspense, lazy, type CSSProperties} from 'react';
import {isMuxUrl, parseMuxPlaybackId, muxPosterUrl} from '@common/enrollmentSite/muxVideo';

// Lazy so the Mux player bundle only loads for viewers who actually reach a
// Mux-backed step; groups without videos never pay for it.
const MuxPlayer = lazy(() => import('@mux/mux-player-react'));

const roundedClip: CSSProperties = {aspectRatio: '16 / 9'};

// Mux's media-controller defaults to a black background. The video's square
// corners don't reach the host's rounded corners, so that black peeks through
// as a dark ring — make it transparent so the corners show the page instead.
const muxStyle = {...roundedClip, '--media-background-color': 'transparent'} as CSSProperties &
  Record<`--${string}`, string>;

// Autoplay-muted looping hero video for a landing/product step. Mux URLs render
// in the Mux player (adaptive HLS + poster); anything else falls back to a
// native <video> so a plain file URL still works.
export function StepVideo({url, className}: {url: string; className?: string}) {
  const box = `w-full overflow-hidden rounded-xl ${className ?? ''}`.trim();
  const playbackId = isMuxUrl(url) ? parseMuxPlaybackId(url) : null;

  if (playbackId) {
    return (
      <Suspense fallback={null}>
        <MuxPlayer
          playbackId={playbackId}
          poster={muxPosterUrl(playbackId)}
          autoPlay="muted"
          muted
          loop
          className={box}
          style={muxStyle}
        />
      </Suspense>
    );
  }

  return (
    <video
      src={url}
      autoPlay
      muted
      loop
      playsInline
      controls
      className={`${box} bg-black`}
      style={roundedClip}
    />
  );
}
